import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  LayoutDashboard, Users, CalendarDays, FileText, Receipt,
  ClipboardList, Wallet, Building2, FolderOpen, Settings,
  LogOut, Bell, Menu, ChevronDown, FileBarChart, BarChart3, KeyRound,
  ListTodo, BookOpen, ShoppingCart, ScrollText, Package, ChevronRight
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/clients', label: 'Clients', icon: Users },
  { path: '/admin/appointments', label: 'Appointments', icon: CalendarDays },
  { path: '/admin/income-tax', label: 'Income Tax', icon: FileText },
  { path: '/admin/gst-pt', label: 'GST & PT', icon: Receipt },
  { path: '/admin/registrations', label: 'Registrations', icon: ClipboardList },
  { path: '/admin/petty-cash', label: 'Petty Cash', icon: Wallet },
  { path: '/admin/rent-tracker', label: 'Rent Tracker', icon: Building2 },
  { path: '/admin/documents', label: 'Documents', icon: FolderOpen },
  { path: '/admin/invoices', label: 'Invoices', icon: FileBarChart },
  { path: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { path: '/admin/tasks', label: 'Tasks', icon: ListTodo },
  { path: '/admin/cash-ledger', label: 'Cash Ledger', icon: BookOpen, adminOnly: true },
  {
    label: 'Procurement', icon: ShoppingCart, group: true,
    children: [
      { path: '/admin/quotations', label: 'Quotations', icon: ScrollText },
      { path: '/admin/purchase-orders', label: 'Purchase Orders', icon: FileText },
      { path: '/admin/stock', label: 'Stock Register', icon: Package },
    ]
  },
  { path: '/admin/settings', label: 'Settings', icon: Settings, adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [procurementOpen, setProcurementOpen] = useState(true);
  const [pwDialog, setPwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleChangePassword = async () => {
    if (!pwForm.current_password || !pwForm.new_password) { toast.error('Fill all fields'); return; }
    if (pwForm.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully');
      setPwDialog(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  const filteredNav = navItems.filter(item => !item.adminOnly || user?.role === 'admin');

  return (
    <div className="flex min-h-screen bg-[#F8FAF7]">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-screen w-64 bg-[#1B2A1B] text-white z-50 flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="p-5 border-b border-[#2F472F]">
          <div className="flex items-center gap-3">
            <img
              src="/images/logo.png"
              alt="SVT & Co"
              className="w-10 h-10 rounded-sm object-contain"
            />
            <div>
              <h1 className="font-heading text-lg font-semibold text-[#D4AF37]">SVT & Co</h1>
              <p className="text-[10px] text-olive-300 tracking-wider uppercase">Tax Consultants</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-3 overflow-y-auto">
          <div className="space-y-0.5">
            {filteredNav.map(item => {
              if (item.group) {
                return (
                  <div key={item.label}>
                    <button
                      onClick={() => setProcurementOpen(o => !o)}
                      className="sidebar-link w-full text-olive-300 hover:text-white"
                      data-testid={`nav-group-${item.label.toLowerCase()}`}
                    >
                      <item.icon size={18} strokeWidth={1.5} />
                      <span className="flex-1 text-left">{item.label}</span>
                      {procurementOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {procurementOpen && (
                      <div className="ml-3 mt-0.5 border-l border-[#2F472F] pl-3 space-y-0.5">
                        {item.children.map(child => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                              `sidebar-link text-sm ${isActive ? 'active' : 'text-olive-300 hover:text-white'}`
                            }
                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <child.icon size={15} strokeWidth={1.5} />
                            <span>{child.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/admin'}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : 'text-olive-300 hover:text-white'}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon size={18} strokeWidth={1.5} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-[#2F472F]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#1B2A1B] font-semibold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-olive-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-1">
            <button
              onClick={() => setPwDialog(true)}
              data-testid="change-password-btn"
              className="flex items-center gap-2 text-olive-400 hover:text-[#D4AF37] text-sm w-full transition-colors duration-200"
            >
              <KeyRound size={16} />
              <span>Change Password</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 text-olive-400 hover:text-red-400 text-sm w-full transition-colors duration-200"
            >
              <LogOut size={16} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Change Password Dialog */}
      <Dialog open={pwDialog} onOpenChange={setPwDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading text-xl">Change Password</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Current Password</Label>
              <Input type="password" value={pwForm.current_password} onChange={(e) => setPwForm(prev => ({ ...prev, current_password: e.target.value }))} className="input-field" placeholder="Enter current password" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">New Password</Label>
              <Input type="password" value={pwForm.new_password} onChange={(e) => setPwForm(prev => ({ ...prev, new_password: e.target.value }))} className="input-field" placeholder="Min 6 characters" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase font-semibold text-[#4F734F]">Confirm New Password</Label>
              <Input type="password" value={pwForm.confirm_password} onChange={(e) => setPwForm(prev => ({ ...prev, confirm_password: e.target.value }))} className="input-field" placeholder="Re-enter new password" data-testid="confirm-password-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleChangePassword} disabled={pwLoading} className="btn-primary flex-1" data-testid="submit-change-password">
                {pwLoading ? 'Changing...' : 'Change Password'}
              </Button>
              <Button variant="outline" onClick={() => setPwDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-[#E6EDE6] px-4 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-sm hover:bg-olive-50"
              data-testid="mobile-menu-btn"
            >
              <Menu size={20} className="text-olive-800" />
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-3">
              <button className="relative p-2 rounded-sm hover:bg-olive-50 transition-colors" data-testid="notifications-btn">
                <Bell size={18} className="text-olive-600" />
              </button>
              <Badge variant="outline" className="text-xs bg-olive-50 text-olive-700 border-olive-200">
                {user?.role === 'admin' ? 'Admin' : 'User'}
              </Badge>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <div className="page-enter">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
